/**
 * Shared post-auth redirect (`?next=`) handling.
 *
 * Several auth landing surfaces let an upstream caller specify where to send
 * the user after sign-in: the magic-link interstitial (`/auth/confirm`), the
 * one-tap login (`/auth/one-tap`), the one-click signup (`/auth/signup`), and
 * — as of REK-1924 (Survey Deeplinks) — the interactive login/onboarding page
 * (`/auth`) and the dashboard auth guard.
 *
 * `sanitizeNext` is the single open-redirect guard for all of them: it only
 * ever returns a SAME-ORIGIN SPA path, falling back to a safe default for
 * empty / cross-origin / malformed input. Keeping ONE implementation avoids
 * the previous four byte-identical copies drifting apart.
 */

/** Default post-auth destination when no (valid) `next` is supplied. */
export const DEFAULT_NEXT = '/dashboard';

/**
 * Coerce a user-supplied `next` value into a safe same-origin SPA path.
 *
 * - Empty / null / undefined -> `defaultNext`.
 * - Absolute URLs whose origin matches `window.location.origin` are stripped
 *   to their `pathname + search + hash`.
 * - Anything else (cross-origin, opaque schemes, junk) falls back to
 *   `defaultNext`. This is the open-redirect guard.
 */
export function sanitizeNext(
  rawNext: string | null | undefined,
  defaultNext: string = DEFAULT_NEXT,
): string {
  if (!rawNext) return defaultNext;
  try {
    const parsed = new URL(rawNext, window.location.origin);
    if (parsed.origin !== window.location.origin) return defaultNext;
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || defaultNext;
  } catch {
    return defaultNext;
  }
}

/**
 * Build a `/auth?...` URL that carries a sanitized `next` so the login /
 * onboarding flow can return the user to where they were headed. The `next`
 * is sanitized first so we never persist a cross-origin redirect into the
 * login URL.
 *
 * @param next   the desired post-auth destination (same-origin path).
 * @param params extra query params (e.g. `{ mode: 'login' }`).
 */
export function buildAuthUrlWithNext(
  next: string,
  params?: Record<string, string>,
): string {
  const search = new URLSearchParams(params);
  search.set('next', sanitizeNext(next));
  return `/auth?${search.toString()}`;
}
